import React, { useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import BadgeIcon from '@mui/icons-material/Badge'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import ErrorIcon from '@mui/icons-material/Error'
import EmailIcon from '@mui/icons-material/Email'
import LightModeIcon from '@mui/icons-material/LightMode'
import LockIcon from '@mui/icons-material/Lock'
import PhoneIcon from '@mui/icons-material/Phone'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Link, useNavigate } from 'react-router'
import { PrimaryButton, SecondaryButton } from '../components/Buttons'
import cnergiLogo from '../resources/CNERGI_PNG@4x.png'
import { registerUser } from '../services/authApi'
import './LoginPage.css'
import './RegisterPage.css'

function RegisterPage({ isDarkMode = false, onToggleTheme }) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false)
  const [formValues, setFormValues] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    contactNo: '',
    password: '',
    confirmPassword: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [resultModal, setResultModal] = useState(null)
  const navigate = useNavigate()

  const handleChange = (event) => {
    const { name, value } = event.target

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (isSubmitting) {
      return
    }

    const firstName = formValues.firstName.trim()
    const middleName = formValues.middleName.trim()
    const lastName = formValues.lastName.trim()
    const email = formValues.email.trim()
    const contactNo = formValues.contactNo.trim()

    if (formValues.password.length < 8) {
      setResultModal({
        type: 'error',
        title: 'Registration unsuccessful',
        message: 'Password must be at least 8 characters.',
      })
      return
    }

    if (formValues.password !== formValues.confirmPassword) {
      setResultModal({
        type: 'error',
        title: 'Registration unsuccessful',
        message: 'Passwords do not match.',
      })
      return
    }

    setIsSubmitting(true)
    setResultModal(null)

    try {
      await registerUser({
        firstName,
        middleName,
        lastName,
        email,
        password: formValues.password,
        contactNo,
      })

      setResultModal({
        type: 'success',
        title: 'Registration successful',
        message: 'Your CNERGI account has been created. You can now sign in.',
        email,
      })
    } catch (error) {
      setResultModal({
        type: 'error',
        title: 'Registration unsuccessful',
        message: error.message || 'Unable to create your account. Please try again.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <main className="login-page register-page">
      <header className="login-page__topbar">
        <Link className="login-page__back-link" to="/">
          <ArrowBackIcon aria-hidden="true" />
          <span>Back to home</span>
        </Link>

        <button
          className="login-page__theme-toggle"
          type="button"
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={onToggleTheme}
        >
          {isDarkMode ? <LightModeIcon aria-hidden="true" /> : <DarkModeIcon aria-hidden="true" />}
          <span>{isDarkMode ? 'Light' : 'Dark'}</span>
        </button>
      </header>

      <section className="login-page__layout register-page__layout" aria-labelledby="register-title">
        <div className="login-page__panel register-page__panel">
          <Link className="login-page__brand" to="/" aria-label="CNERGI home">
            <img src={cnergiLogo} alt="CNERGI" />
            <span>Intelligent Energy. VISIBLE Impact</span>
          </Link>

          <div className="login-page__intro">
            <p>CNERGI EMS Portal</p>
            <h1 id="register-title">Create your operations account.</h1>
          </div>

          <form className="login-page__form" onSubmit={handleSubmit}>
            <div className="register-page__name-grid">
              <label className="login-page__field">
                <span>First name</span>
                <div className="login-page__input-wrap">
                  <BadgeIcon aria-hidden="true" />
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First name"
                    autoComplete="given-name"
                    value={formValues.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </label>

              <label className="login-page__field">
                <span>Middle name</span>
                <div className="login-page__input-wrap">
                  <BadgeIcon aria-hidden="true" />
                  <input
                    type="text"
                    name="middleName"
                    placeholder="Middle name"
                    autoComplete="additional-name"
                    value={formValues.middleName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </label>

              <label className="login-page__field">
                <span>Last name</span>
                <div className="login-page__input-wrap">
                  <BadgeIcon aria-hidden="true" />
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last name"
                    autoComplete="family-name"
                    value={formValues.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </label>
            </div>

            <label className="login-page__field">
              <span>Email address</span>
              <div className="login-page__input-wrap">
                <EmailIcon aria-hidden="true" />
                <input
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  autoComplete="email"
                  value={formValues.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </label>

            <label className="login-page__field">
              <span>Contact number</span>
              <div className="login-page__input-wrap">
                <PhoneIcon aria-hidden="true" />
                <input
                  type="tel"
                  name="contactNo"
                  placeholder="+63 912 345 6789"
                  autoComplete="tel"
                  value={formValues.contactNo}
                  onChange={handleChange}
                  required
                />
              </div>
            </label>

            <label className="login-page__field">
              <span>Password</span>
              <div className="login-page__input-wrap">
                <LockIcon aria-hidden="true" />
                <input
                  type={isPasswordVisible ? 'text' : 'password'}
                  name="password"
                  placeholder="Create a password"
                  autoComplete="new-password"
                  value={formValues.password}
                  onChange={handleChange}
                  minLength={8}
                  required
                />
                <button
                  className="login-page__password-toggle"
                  type="button"
                  aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                  onClick={() => setIsPasswordVisible((current) => !current)}
                >
                  {isPasswordVisible ? <VisibilityOffIcon aria-hidden="true" /> : <VisibilityIcon aria-hidden="true" />}
                </button>
              </div>
            </label>

            <label className="login-page__field">
              <span>Confirm password</span>
              <div className="login-page__input-wrap">
                <LockIcon aria-hidden="true" />
                <input
                  type={isConfirmPasswordVisible ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                  value={formValues.confirmPassword}
                  onChange={handleChange}
                  minLength={8}
                  required
                />
                <button
                  className="login-page__password-toggle"
                  type="button"
                  aria-label={isConfirmPasswordVisible ? 'Hide password' : 'Show password'}
                  onClick={() => setIsConfirmPasswordVisible((current) => !current)}
                >
                  {isConfirmPasswordVisible ? <VisibilityOffIcon aria-hidden="true" /> : <VisibilityIcon aria-hidden="true" />}
                </button>
              </div>
            </label>

            <PrimaryButton className="login-page__submit" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating account...' : 'Create Account'}
            </PrimaryButton>

            <p className="register-page__signin">
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          </form>
        </div>

      </section>

      {resultModal ? (
        <div
          className="register-result-modal"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && resultModal.type === 'error') {
              setResultModal(null)
            }
          }}
        >
          <section
            className={`register-result-modal__dialog register-result-modal__dialog--${resultModal.type}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="register-result-title"
            aria-describedby="register-result-message"
          >
            {resultModal.type === 'error' ? (
              <button
                className="register-result-modal__close"
                type="button"
                aria-label="Close registration result"
                onClick={() => setResultModal(null)}
              >
                <CloseIcon aria-hidden="true" />
              </button>
            ) : null}

            <div className="register-result-modal__icon" aria-hidden="true">
              {resultModal.type === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
            </div>

            <div className="register-result-modal__copy">
              <h2 id="register-result-title">{resultModal.title}</h2>
              <p id="register-result-message">{resultModal.message}</p>
            </div>

            <div className="register-result-modal__actions">
              {resultModal.type === 'success' ? (
                <PrimaryButton
                  type="button"
                  onClick={() => navigate('/login', {
                    replace: true,
                    state: { email: resultModal.email },
                  })}
                >
                  Continue to Sign In
                </PrimaryButton>
              ) : (
                <SecondaryButton type="button" onClick={() => setResultModal(null)}>
                  Review Details
                </SecondaryButton>
              )}
            </div>
          </section>
        </div>
      ) : null}
    </main>
  )
}

export default RegisterPage
